import { Layers, List, Trash2 } from "lucide-react";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { Outlet, useMatch, useParams } from "react-router-dom";

import { HeaderNavElement } from "@/components/_molecules/header-nav-element";
import { ListHeaderTitle } from "@/components/_molecules/list-header-title";
import { ListHeaderBar } from "@/components/_organisms/list-header-bar";
import { HeaderLayout } from "@/components/_templates/header-layout";
import { FeatureFlagToggle } from "@/domain/_shared/feature-flag/components/feature-flag-toggle";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { MunicipalPropertyCreateDialog } from "@/domain/municipal-property/components/municipal-property-create-dialog";
import { MunicipalPropertyRegisterActions } from "@/domain/municipal-property/components/municipal-property-register-actions";
import { MunicipalPropertyRegisterCreateDialog } from "@/domain/municipal-property/components/municipal-property-register-create-dialog";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { makeWorkspaceUrl } from "@/domain/workspace/lib/make-workspace-url";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { MUNICIPAL_PROPERTY_CONSUMPTION_FEATURE_FLAG_ID, MUNICIPAL_PROPERTY_FEATURE_FLAG_ID } from "@ark/shared";

import { MUNICIPAL_PROPERTY_BASE_PATH } from "./municipal-property-base-path";
import { MUNICIPAL_PROPERTY_CATALOG_PATH } from "./municipal-property-catalog-path";
import { MUNICIPAL_PROPERTY_REGISTER_LIST_PATH } from "./municipal-property-register-list-path";

interface MunicipalPropertyLayoutProps {
  section: "catalog" | "register";
}

export const MunicipalPropertyLayout = ({ section }: MunicipalPropertyLayoutProps) => {
  const { registerId }: { registerId?: string } = useParams<{ registerId: string }>();
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const { t } = useDomainTranslation("municipal-property");

  const catalogPath = makeWorkspaceUrl({
    workspace: currentWorkspace,
    path: `${MUNICIPAL_PROPERTY_BASE_PATH}/${MUNICIPAL_PROPERTY_CATALOG_PATH}`,
  });
  const registerListPath = makeWorkspaceUrl({
    workspace: currentWorkspace,
    path: `${MUNICIPAL_PROPERTY_BASE_PATH}/${MUNICIPAL_PROPERTY_REGISTER_LIST_PATH}`,
  });
  const isRegisterList: boolean = useMatch({ path: registerListPath, end: true }) !== null;
  const isCatalogList: boolean = useMatch({ path: catalogPath, end: true }) !== null;
  const annualTrashPath: string = makeWorkspaceUrl({
    workspace: currentWorkspace,
    path: `${MUNICIPAL_PROPERTY_BASE_PATH}/${MUNICIPAL_PROPERTY_REGISTER_LIST_PATH}/years/trash`,
  });
  const isAnnualTrash: boolean = useMatch({ path: annualTrashPath, end: true }) !== null;
  const trashPath: string = makeWorkspaceUrl({
    workspace: currentWorkspace,
    path: `${MUNICIPAL_PROPERTY_BASE_PATH}/${section === "register" ? `${MUNICIPAL_PROPERTY_REGISTER_LIST_PATH}/trash` : "trash"}`,
  });
  const currentTrashPath: string = isAnnualTrash ? annualTrashPath : trashPath;
  const isTrashSection: boolean = useMatch({ path: currentTrashPath, end: true }) !== null;
  const sectionTitle = t(section === "register" ? "register.title" : "catalog.title");
  useDocumentTitle(sectionTitle);

  return (
    <div className="flex h-full max-h-full flex-col overflow-y-hidden">
      <HeaderLayout>
        <ListHeaderBar
          pageName={sectionTitle}
          title={<ListHeaderTitle icon={HiOutlineBuildingOffice2} label={sectionTitle} />}
          actions={
            registerId ? (
              <MunicipalPropertyRegisterActions registerId={registerId} />
            ) : isRegisterList ? (
              <MunicipalPropertyRegisterCreateDialog workspace={currentWorkspace} />
            ) : isCatalogList ? (
              <MunicipalPropertyCreateDialog workspace={currentWorkspace} />
            ) : null
          }
        >
          <FeatureFlagToggle flag={MUNICIPAL_PROPERTY_FEATURE_FLAG_ID} isDevelopmentBypassAllowed={false}>
            <HeaderNavElement to={catalogPath} icon={List} end>
              {t("catalog.tabCatalog")}
            </HeaderNavElement>
          </FeatureFlagToggle>
          {section === "register" && (
            <FeatureFlagToggle flag={MUNICIPAL_PROPERTY_CONSUMPTION_FEATURE_FLAG_ID} isDevelopmentBypassAllowed={false}>
              <HeaderNavElement to={registerListPath} icon={Layers} end={isTrashSection}>
                {t("register.tab")}
              </HeaderNavElement>
            </FeatureFlagToggle>
          )}
          <FeatureFlagToggle
            flag={
              section === "register"
                ? MUNICIPAL_PROPERTY_CONSUMPTION_FEATURE_FLAG_ID
                : MUNICIPAL_PROPERTY_FEATURE_FLAG_ID
            }
            isDevelopmentBypassAllowed={false}
          >
            <FeatureFlagToggle flag="enable-trash">
              <HeaderNavElement to={currentTrashPath} icon={Trash2} end>
                {t("catalog.trashTab")}
              </HeaderNavElement>
            </FeatureFlagToggle>
          </FeatureFlagToggle>
        </ListHeaderBar>
      </HeaderLayout>
      <Outlet />
    </div>
  );
};
